import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, BellRing, CalendarClock, Check, Pencil, Plus, Power, Repeat, Search, Star, Trash2, UserRound, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, type Device, type LineupEntry, type Room } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, PageHeader } from '../components/ui';

interface Wakeup {
  deviceId: string;
  deviceName: string;
  room: { id: string; number: string } | null;
  guest: string | null;
  vip: boolean;
  time: string;
  enabled: boolean;
  daily: boolean;
  channelNumber: number | null;
  setBy: string;
  updatedAt: string;
}

/** A wake-up call per room: the room's TVs all ring (the API stores one per TV). */
interface RoomCall {
  key: string;
  room: { id: string; number: string } | null;
  devices: Wakeup[];
  first: Wakeup;
}

/** Minutes until the next ring in the hotel's time zone, or null when off. */
function nextRing(time: string, tz: string, enabled: boolean) {
  if (!enabled) return null;
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
  const now = Number(p.find((x) => x.type === 'hour')?.value) * 60 + Number(p.find((x) => x.type === 'minute')?.value);
  const [h, m] = time.split(':').map(Number);
  let d = h! * 60 + m! - now;
  if (d <= 0) d += 1440;
  return d;
}
const inText = (min: number) => (min < 60 ? `in ${min} min` : `in ${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`);

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function WakeupCallsPage() {
  const site = useSite();
  return (
    <>
      <PageHeader title="Wake-up calls" sub="Every wake-up call in the hotel. The TV switches on at the time, plays the chosen channel and shows the alarm until the guest stops it." />
      <NeedsSite>{site && <Calls key={site.id} siteId={site.id} tz={site.timezone} />}</NeedsSite>
    </>
  );
}

function Calls({ siteId, tz }: { siteId: string; tz: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const op = can('OPERATOR');
  const list = useQuery({ queryKey: ['wakeups', siteId], queryFn: () => api.get<{ items: Wakeup[] }>(`/sites/${siteId}/wakeups`).then((r) => r.items), refetchInterval: 30_000 });
  const rooms = useQuery({ queryKey: ['rooms', siteId, null], queryFn: () => api.get<{ items: Room[] }>(`/sites/${siteId}/rooms`) });
  const devices = useQuery({ queryKey: ['devices', siteId], queryFn: () => api.get<{ items: Device[] }>(`/sites/${siteId}/devices`) });
  const lineup = useQuery({ queryKey: ['lineup', siteId], queryFn: () => api.get<{ items: LineupEntry[] }>(`/sites/${siteId}/lineup`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['wakeups', siteId] });
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');
  const [editing, setEditing] = useState<string | null>(null); // room id or '' for a new call
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  // One row per room (or per TV without a room).
  const byRoom = new Map<string, RoomCall>();
  for (const w of list.data ?? []) {
    const k = w.room?.id ?? `tv:${w.deviceId}`;
    const c = byRoom.get(k);
    if (c) c.devices.push(w);
    else byRoom.set(k, { key: k, room: w.room, devices: [w], first: w });
  }
  const all = [...byRoom.values()].sort((a, b) => (nextRing(a.first.time, tz, a.first.enabled) ?? 1e9) - (nextRing(b.first.time, tz, b.first.enabled) ?? 1e9));
  const items = all.filter((c) => {
    const s = q.trim().toLowerCase();
    if (s && ![c.room?.number, c.first.guest, c.first.deviceName].some((x) => x?.toLowerCase().includes(s))) return false;
    if (show === 'on' && !c.first.enabled) return false;
    if (show === 'off' && c.first.enabled) return false;
    if (show === 'daily' && !c.first.daily) return false;
    return true;
  });
  const roomDevices = (roomId: string) => (devices.data?.items ?? []).filter((d) => d.roomId === roomId);
  const channelName = (n: number | null) => (n === null ? 'Current channel' : (() => {
    const e = (lineup.data?.items ?? []).find((x) => x.number === n);
    return e ? `${n} ${e.channel.name}` : `Channel ${n}`;
  })());

  const toggle = useMutation({
    mutationFn: async (c: RoomCall) => {
      for (const w of c.devices) await api.put(`/devices/${w.deviceId}/wakeup`, { time: w.time, daily: w.daily, channelNumber: w.channelNumber, enabled: !c.first.enabled });
    },
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: async (c: RoomCall) => {
      for (const w of c.devices) await api.delete(`/devices/${w.deviceId}/wakeup`);
    },
    onSuccess: invalidate,
  });
  const soon = all.filter((c) => {
    const n = nextRing(c.first.time, tz, c.first.enabled);
    return n !== null && n <= 12 * 60;
  }).length;

  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<AlarmClock size={22} />} tone="violet" value={all.length} label="Wake-up calls" sub="Rooms with a call set" />
        <StatCard icon={<BellRing size={22} />} tone="amber" value={soon} label="Next 12 hours" sub="Calls that will ring" />
        <StatCard icon={<Repeat size={22} />} tone="blue" value={all.filter((c) => c.first.daily).length} label="Every day" sub="Repeat until cancelled" />
        <StatCard icon={<UserRound size={22} />} tone="green" value={all.filter((c) => c.first.setBy === 'GUEST').length} label="Set by guests" sub="From the TV" />
      </div>

      <div className="wake-layout">
        <div className="card fill">
          <div className="pkg-head">
            <span className="row" style={{ gap: 8, flex: 1 }}>
              <span className="pkg-head-title">Scheduled calls</span>
              <span className="pkg-count">{all.length}</span>
            </span>
            {op && (
              <button className="btn primary" onClick={() => setEditing('')}>
                <Plus size={16} /> New wake-up call
              </button>
            )}
          </div>
          <div className="filters" style={{ marginTop: 14 }}>
            <label className="search">
              <Search size={16} />
              <input placeholder="Search room or guest…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Select value={show} onChange={(e) => setShow(e.target.value)} aria-label="Show">
              <option value="all">All calls</option>
              <option value="on">On</option>
              <option value="off">Turned off</option>
              <option value="daily">Every day</option>
            </Select>
          </div>
          {list.isLoading ? (
            <Loading />
          ) : items.length === 0 ? (
            <Empty>{all.length ? 'No call matches.' : 'No wake-up calls. Guests set them from the TV, or use “New wake-up call”.'}</Empty>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="fac-table">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Guest</th>
                    <th>Time</th>
                    <th>Repeat</th>
                    <th>Channel</th>
                    <th>Set by</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => {
                    const w = c.first;
                    const n = nextRing(w.time, tz, w.enabled);
                    return (
                      <tr key={c.key} style={!w.enabled ? { opacity: 0.6 } : undefined} className={editing === c.room?.id ? 'selected' : ''}>
                        <td>
                          <strong>{c.room ? `Room ${c.room.number}` : w.deviceName}</strong>
                          {c.devices.length > 1 && (
                            <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                              {c.devices.length} TVs
                            </span>
                          )}
                        </td>
                        <td>
                          {w.guest ?? <span className="muted">—</span>}
                          {w.vip && (
                            <span className="badge brand vip-badge">
                              <Star size={11} /> VIP
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="wake-time">{w.time}</span>
                        </td>
                        <td>{w.daily ? 'Every day' : 'Once'}</td>
                        <td className="muted" style={{ fontSize: 13 }}>
                          {channelName(w.channelNumber)}
                        </td>
                        <td>
                          <span className="chip" style={w.setBy === 'GUEST' ? { color: '#047857', background: '#e7f8f1' } : { color: '#1d4ed8', background: '#e8f0ff' }}>
                            {w.setBy === 'GUEST' ? 'Guest (TV)' : 'Front desk'}
                          </span>
                        </td>
                        <td>
                          <span className={`state-pill ${w.enabled ? 'online' : 'offline'}`} style={{ whiteSpace: 'nowrap' }}>
                            <span className="state-dot" /> {w.enabled ? `Rings ${inText(n!)}` : 'Turned off'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {op && (
                            <>
                              {c.room && (
                                <button className="icon-btn" title="Edit" onClick={() => setEditing(c.room!.id)}>
                                  <Pencil size={16} />
                                </button>
                              )}
                              <button className="icon-btn" title={w.enabled ? 'Turn off' : 'Turn on'} onClick={() => toggle.mutate(c)}>
                                <Power size={16} />
                              </button>
                              <button
                                className="icon-btn danger-icon"
                                title="Cancel"
                                onClick={() => ask(`Cancel the wake-up call for ${c.room ? `room ${c.room.number}` : w.deviceName}? The TV will not ring.`, () => cancel.mutate(c), { action: 'Cancel call', danger: true })}
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <ErrorBox error={list.error ?? toggle.error ?? cancel.error} />
        </div>

        {editing !== null && op && (
          <WakeupForm
            key={editing}
            rooms={(rooms.data?.items ?? []).filter((r) => roomDevices(r.id).length > 0).sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }))}
            roomId={editing}
            existing={editing ? byRoom.get(editing)?.first ?? null : null}
            channels={lineup.data?.items ?? []}
            roomDevices={roomDevices}
            onClose={() => setEditing(null)}
            onSaved={() => {
              void invalidate();
              setEditing(null);
            }}
          />
        )}
      </div>
    </>
  );
}

function WakeupForm({
  rooms,
  roomId: initialRoom,
  existing,
  channels,
  roomDevices,
  onClose,
  onSaved,
}: {
  rooms: Room[];
  roomId: string;
  existing: Wakeup | null;
  channels: LineupEntry[];
  roomDevices: (roomId: string) => Device[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [roomId, setRoomId] = useState(initialRoom);
  const [time, setTime] = useState(existing?.time ?? '07:00');
  const [daily, setDaily] = useState(existing?.daily ?? false);
  const [channel, setChannel] = useState(existing?.channelNumber ? String(existing.channelNumber) : '');
  const tvs = roomId ? roomDevices(roomId) : [];
  const save = useMutation({
    mutationFn: async () => {
      for (const d of tvs) await api.put(`/devices/${d.id}/wakeup`, { time, daily, enabled: true, channelNumber: channel ? Number(channel) : null });
    },
    onSuccess: onSaved,
  });
  return (
    <div className="card wake-form">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 17 }}>{existing ? 'Edit wake-up call' : 'New wake-up call'}</strong>
        <button className="icon-btn" onClick={onClose} title="Close">
          <X size={16} />
        </button>
      </div>
      <div className="pw-form">
        <label>
          <span className="camp-label">Room</span>
          <Select value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={!!existing} aria-label="Room">
            <option value="">Choose a room…</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                Room {r.number}
              </option>
            ))}
          </Select>
          {roomId && (
            <span className="camp-hint">
              {tvs.length} TV{tvs.length === 1 ? '' : 's'} in the room will ring: {tvs.map((d) => d.name).join(', ')}
            </span>
          )}
        </label>
        <label>
          <span className="camp-label">Time (hotel time)</span>
          <input type="time" className="wake-input" value={time} onChange={(e) => setTime(e.target.value)} required />
        </label>
        <div className="role-pick" style={{ marginTop: 0 }}>
          <button type="button" className={!daily ? 'on' : ''} onClick={() => setDaily(false)}>
            <strong>Once</strong>
            <span>Next time it is {time}</span>
            {!daily && <Check size={16} />}
          </button>
          <button type="button" className={daily ? 'on' : ''} onClick={() => setDaily(true)}>
            <strong>Every day</strong>
            <span>Until cancelled or check-out</span>
            {daily && <Check size={16} />}
          </button>
        </div>
        <label>
          <span className="camp-label">Channel</span>
          <Select value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Channel">
            <option value="">The channel the TV was on</option>
            {channels
              .filter((c) => c.enabled)
              .map((c) => (
                <option key={c.id} value={c.number}>
                  {c.number} {c.channel.name}
                </option>
              ))}
          </Select>
        </label>
        <span className="camp-hint">
          <CalendarClock size={13} style={{ verticalAlign: -2 }} /> Wake-up calls end at check-out.
        </span>
        <ErrorBox error={save.error} />
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!roomId || !tvs.length || !/^\d\d:\d\d$/.test(time) || save.isPending} onClick={() => save.mutate()}>
            <AlarmClock size={16} /> {save.isPending ? 'Saving…' : 'Save wake-up call'}
          </button>
        </div>
      </div>
    </div>
  );
}
